// Phase APP-2 · Approval detail drawer (10 spec sections).
// Audit Trail block is read-only DOM — NO buttons/inputs/textareas inside.
import { useEffect, useState } from "react";
import { X, Loader2, RefreshCw, ChevronDown, ChevronRight,
          User, Building2, Inbox, FileText, History, Bell, ShieldAlert, Shield } from "lucide-react";
import useTimedFetch from "../../../hooks/useTimedFetch";
import StatusBadge from "../../hub/StatusBadge";
import RejectApprovalModal from "./RejectApprovalModal";
import ApproveApprovalModal from "./ApproveApprovalModal";
import FounderOverrideModal from "./FounderOverrideModal";

const ORANGE = "#FF6B00";

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Section({ title, Icon, count, defaultOpen = true, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500"/> : <ChevronRight className="h-3.5 w-3.5 text-slate-500"/>}
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-500"/>}
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {typeof count === "number" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-[#C2410C] font-semibold">
            {count}
          </span>
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </section>
  );
}

export default function ApprovalDetailDrawer({ requestId, open, onClose, onChanged, readOnly = false, founderMode = false }) {
  const { data, loading, error, reload } = useTimedFetch(
    requestId ? `/approvals/${requestId}` : null,
    { skip: !open || !requestId });

  const { data: notifs, reload: reloadNotifs } = useTimedFetch(
    requestId ? "/approvals/notifications" : null, { skip: !open || !requestId });

  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const d = data || {};
  const p = d.entity_payload || {};
  const history = d.approval_history || [];
  const audit = d.audit_log || [];
  const isPacker = d.approval_type === "packer_request";
  const isOpen = ["pending", "rejected_1", "resubmitted_pending"].includes(d.status);

  const rejectionHistory = history.filter((h) => h.action === "rejected");
  const myNotifs = (notifs?.rows || []).filter((n) => n.approval_request_id === requestId);

  const onActionDone = () => {
    reload(); reloadNotifs(); onChanged?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="approval-detail-drawer" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true"/>
      <aside className="relative ml-auto w-full max-w-3xl h-full bg-slate-50 shadow-2xl flex flex-col">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 shrink-0">
          <span className="text-xs uppercase tracking-wider text-slate-500">Approval</span>
          <span className="text-sm font-mono text-slate-700 ml-1" data-testid="approval-detail-request-id">{requestId}</span>
          <button onClick={onClose} aria-label="Close"
                  data-testid="approval-detail-close"
                  className="ml-auto p-1.5 rounded-full hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
            <X className="h-5 w-5"/>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-3 pb-24">
          {loading && !data && (
            <div className="space-y-3" data-testid="approval-detail-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse"/>
              ))}
            </div>
          )}
          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center"
                 data-testid="approval-detail-error">
              <div className="text-rose-700 font-semibold mb-1">Couldn't load</div>
              <div className="text-xs text-rose-600 mb-3">{error.message}</div>
              <button onClick={reload} data-testid="approval-detail-retry"
                      className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white"
                      style={{ backgroundColor: ORANGE }}>
                <RefreshCw className="h-3 w-3"/> Retry
              </button>
            </div>
          )}
          {data && (
            <>
              {/* 1. Header */}
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="approval-detail-header">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 text-base">
                    {isPacker ? "Packer Request" : "Micro Hub Suggestion"}
                  </span>
                  <StatusBadge status={d.status} size="sm" testid="approval-detail-status"/>
                  {(d.resubmission_count || 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                          data-testid="approval-detail-resub-count">
                      Resubmitted {d.resubmission_count}/1
                    </span>
                  )}
                </div>
              </div>

              {/* 2. Submitter info */}
              <Section title="Submitter" Icon={User} testid="approval-detail-submitter">
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <Field label="Name"      value={d.submitted_by_name}/>
                  <Field label="Role"      value={d.submitted_by_role}/>
                  <Field label="Main hub"  value={d.main_hub_id || "—"}/>
                  <Field label="Submitted" value={`${relTime(d.submitted_at)} · ${d.submitted_at}`}/>
                  {isPacker && p.micro_hub_id && (
                    <Field label="Micro hub" value={p.micro_hub_id}/>
                  )}
                </dl>
              </Section>

              {/* 3. Entity payload */}
              <Section title="Entity payload" Icon={FileText} testid="approval-detail-payload">
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(p).map(([k, v]) => (
                    <Field key={k} label={k} value={String(v ?? "—")}/>
                  ))}
                </dl>
              </Section>

              {/* 4. Previous rejection history */}
              {rejectionHistory.length > 0 && (
                <Section title="Rejection history" Icon={ShieldAlert}
                          count={rejectionHistory.length}
                          testid="approval-detail-rejection-history">
                  <ul className="space-y-2">
                    {rejectionHistory.map((r, i) => (
                      <li key={i}
                          data-testid={`approval-detail-rejection-${i}`}
                          className="border border-rose-200 bg-rose-50 rounded-lg p-2 text-xs">
                        <div className="flex justify-between">
                          <span className="font-semibold text-rose-800 capitalize">
                            {(r.rejection_reason || {}).category?.replace(/_/g, " ")}
                          </span>
                          <span className="text-[10px] text-slate-500">{relTime(r.ts)}</span>
                        </div>
                        <div className="text-slate-700 mt-1">
                          {(r.rejection_reason || {}).remarks}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          by {r.actor_name} · {r.actor_role}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* 5. Action timeline */}
              <Section title="Action timeline" Icon={History} testid="approval-detail-timeline">
                <ul className="space-y-1.5">
                  {history.map((h, i) => {
                    const isOverride = h.action === "founder_override_approved";
                    return (
                      <li key={i}
                          data-testid={`approval-detail-timeline-row-${i}`}
                          className="flex items-start gap-2 text-xs">
                        <div className={"w-1.5 h-1.5 rounded-full mt-1.5 " +
                                          (isOverride ? "bg-purple-600" : "bg-[#FF6B00]")}/>
                        <div className="flex-1">
                          <div className="text-slate-700">
                            {isOverride && (
                              <span data-testid="override-badge"
                                    className="mr-1.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 font-semibold">
                                <Shield className="h-3 w-3"/> Founder Override
                              </span>
                            )}
                            <span className="font-semibold capitalize">{h.action.replace(/_/g, " ")}</span>
                            {" by "}
                            <span className="font-medium">{h.actor_name}</span>
                            <span className="text-slate-400"> · {h.actor_role}</span>
                          </div>
                          <div className="text-[10px] text-slate-500">{h.ts}</div>
                          {isOverride && h.justification && (
                            <blockquote data-testid="override-justification"
                                          data-readonly="true"
                                          className="mt-1 pl-2 border-l-2 border-purple-300 bg-purple-50 text-purple-900 italic px-2 py-1 rounded">
                              {h.justification}
                            </blockquote>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Section>

              {/* 6. Notification history */}
              <Section title="Notifications fired" Icon={Bell}
                        count={myNotifs.length}
                        testid="approval-detail-notifications">
                {myNotifs.length === 0
                  ? <div className="text-xs text-slate-400">No notifications recorded.</div>
                  : (
                    <ul className="space-y-1.5">
                      {myNotifs.map((n, i) => (
                        <li key={n.id || i} className="text-xs flex items-start gap-2">
                          <span className={"w-1.5 h-1.5 rounded-full mt-1.5 " +
                                            (n.read ? "bg-slate-300" : "bg-[#FF6B00]")}/>
                          <div className="flex-1">
                            <div className="text-slate-700">
                              <span className="font-semibold">{n.event}</span>
                              {" → "}
                              {n.recipient_user_id ? "user" : n.recipient_role}
                              {n.recipient_main_hub_id && (
                                <span className="text-slate-400"> · {n.recipient_main_hub_id}</span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500">{n.ts}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
              </Section>

              {/* 7. Audit trail (READ-ONLY DOM — no buttons/inputs) */}
              <Section title="Audit trail" Icon={Inbox} testid="approval-detail-audit-section"
                        defaultOpen={false}>
                <div data-testid="audit-trail-readonly"
                     data-readonly="true"
                     className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs font-mono">
                  {audit.length === 0
                    ? <span className="text-slate-400 not-italic">No audit entries.</span>
                    : (
                      <ol className="space-y-1 list-decimal list-inside marker:text-slate-400">
                        {audit.map((a, i) => (
                          <li key={i} data-testid={`audit-row-${i}`}>
                            <span className="text-[#C2410C] font-semibold">{a.event}</span>
                            {" — "}
                            <span className="text-slate-700">{a.actor_name}</span>
                            <span className="text-slate-400"> · {a.actor_role} · {a.ts}</span>
                            {a.justification && (
                              <div className="ml-4 mt-0.5 text-purple-700 not-italic">
                                ↳ {a.justification}
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                </div>
              </Section>

              {/* 8. Main hub chip clickable */}
              {d.main_hub_id && (
                <Section title="Routing scope" Icon={Building2}
                          testid="approval-detail-scope">
                  <div className="text-xs">
                    Main hub: <span className="font-mono">{d.main_hub_id}</span>
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* Sticky bottom action bar */}
        {data && founderMode && !readOnly && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 shrink-0"
               data-testid="approval-detail-actions">
            <button onClick={() => setOverrideOpen(true)}
                    disabled={!isOpen}
                    title={isOpen
                      ? "Founder override — bypasses the normal flow"
                      : `Override unavailable: status is "${d.status}". Only pending / rejected_1 / resubmitted_pending can be overridden.`}
                    data-testid="approval-detail-override-btn"
                    data-disabled-reason={isOpen ? "" : `terminal:${d.status}`}
                    className="w-full text-sm font-semibold px-3 py-2.5 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#8B5CF6" }}>
              <Shield className="h-4 w-4"/> Founder Override Approve
            </button>
          </div>
        )}
        {data && !founderMode && !readOnly && isOpen && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex gap-2 shrink-0"
               data-testid="approval-detail-actions">
            <button onClick={() => setRejectOpen(true)}
                    data-testid="approval-detail-reject-btn"
                    className="flex-1 text-sm font-semibold px-3 py-2.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700">
              Reject
            </button>
            <button onClick={() => setApproveOpen(true)}
                    data-testid="approval-detail-approve-btn"
                    className="flex-1 text-sm font-semibold px-3 py-2.5 rounded-lg text-white"
                    style={{ backgroundColor: ORANGE }}>
              Approve
            </button>
          </div>
        )}
        {data && readOnly && (
          <div className="sticky bottom-0 bg-slate-100 border-t border-slate-200 px-4 py-3 text-center text-xs text-slate-600"
               data-testid="approval-detail-readonly-banner">
            Read-only view
          </div>
        )}
      </aside>

      {/* Modals */}
      <RejectApprovalModal open={rejectOpen} onClose={() => setRejectOpen(false)}
                           request={data} onDone={onActionDone}/>
      <ApproveApprovalModal open={approveOpen} onClose={() => setApproveOpen(false)}
                              request={data} onDone={onActionDone}/>
      <FounderOverrideModal open={overrideOpen} onClose={() => setOverrideOpen(false)}
                              request={data} onDone={onActionDone}/>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-800 break-words">{value || "—"}</dd>
    </div>
  );
}
